import {
  filterTypesEnum,
  type FilterHierarchyStatusType,
} from "@/types/common/filters";

export const buildHierarchyStatusString = (
  data: FilterHierarchyStatusType,
): string => {
  return data.groups
    .flatMap((group) =>
      group.status
        .map((parent) => {
          const selectedChildren =
            parent.children
              ?.filter((child) => child.value)
              .map((child) => child.field) ?? [];

          if (selectedChildren.length === 0) return null;

          return `${parent.field
            .replace(/-node$/i, "")
            .replace(/-/g, "_")
            .toUpperCase()}:${selectedChildren.join(",")}`;
        })
        .filter(Boolean),
    )
    .join("|");
};

export const parseHierarchyStatusString = (
  status: string,
): Record<string, string[]> => {
  if (!status.trim()) return {};

  return status.split("|").reduce<Record<string, string[]>>((acc, item) => {
    const [parentKey, childrenRaw] = item.split(":");

    if (!parentKey) return acc;

    const key = parentKey.trim();

    const values = childrenRaw
      ? childrenRaw
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
      : [];

    acc[key] = [...(acc[key] ?? []), ...values];

    return acc;
  }, {});
};

export const getHierarchyParentKey = (label: string): string => {
  const map: Record<string, string> = {
    "Formulario interno": "FORM",
    "Formulario externo": "FORM",
    Revisión: "REVISION",
    "Solicitud de plazo": "REVISION",
    Pago: "PAYMENT",
    Firma: "SIGN_DOCUMENT",
    Resolución: "RESOLUTION",
    "Acciones del sistema": "GENERAL",
  };

  return map[label] ?? label.replace(/\s+/g, "_").toUpperCase();
};

export const buildHierarchyStatusFilter = (initialStatus: string) => {
  const parsedStatus = parseHierarchyStatusString(initialStatus);

  const groups = hierarchyStatusTemplate.groups.map((group) => ({
    ...group,
    status: group.status.map((parent) => {
      const parentKey = getHierarchyParentKey(parent.label);

      let selectedChildren = parsedStatus[parentKey] ?? [];

      if (parentKey === "FORM") {
        if (parent.label === "Formulario externo") {
          selectedChildren = selectedChildren.filter((s) =>
            s.startsWith("EXTERNAL"),
          );
        }

        if (parent.label === "Formulario interno") {
          selectedChildren = selectedChildren.filter(
            (s) => !s.startsWith("EXTERNAL"),
          );
        }
      }

      const children =
        parent.children?.map((child) => ({
          ...child,
          value: selectedChildren.includes(child.field),
        })) ?? [];

      return {
        ...parent,
        value: children.every((child) => child.value),
        children,
      };
    }),
  }));

  const isUpdated = initialStatus.trim() !== "";

  return {
    id: 2,
    type: filterTypesEnum.hierarchyStatus,
    label: "Estado de solicitud",
    field: "status",
    result: initialStatus,
    isUpdated,
    groups,
  };
};

export const hierarchyStatusTemplate = {
  id: 2,
  type: filterTypesEnum.hierarchyStatus,
  label: "Estado de solicitud",
  result: "",
  field: "status",
  isUpdated: false,
  groups: [
    {
      label: "Interno",
      status: [
        {
          label: "Formulario interno",
          field: "form-node",
          value: false,
          children: [
            {
              label: "Pendiente",
              field: "PENDING",
              value: false,
              icon: {
                name: "clock",
                type: "far",
                color: "info",
              },
            },
            {
              label: "En proceso",
              field: "IN_PROCESS",
              value: false,
              icon: {
                name: "arrows-repeat",
                type: "far",
                color: "action",
              },
            },
          ],
        },
        {
          label: "Formulario externo",
          field: "form-node",
          value: false,
          children: [
            {
              label: "Pendiente de ingreso",
              field: "EXTERNAL_FORM_PENDING_ENTRY",
              value: false,
              icon: {
                name: "clock",
                type: "far",
                color: "info",
              },
            },
            {
              label: "Ingresado",
              field: "EXTERNAL_FORM_RECEIVED",
              value: false,
              icon: {
                name: "envelope-dot",
                type: "far",
                color: "action",
              },
            },
          ],
        },
        {
          label: "Revisión",
          field: "revision-node",
          value: false,
          children: [
            {
              label: "Pendiente",
              field: "PENDING",
              value: false,
              icon: {
                name: "clock",
                type: "far",
                color: "info",
              },
            },
            {
              label: "En revisión",
              field: "UNDER_REVIEW",
              value: false,
              icon: {
                name: "arrows-repeat",
                type: "far",
                color: "action",
              },
            },
            {
              label: "Esperando visto bueno",
              field: "IN_LEADER_REVIEW",
              value: false,
              icon: {
                name: "clock",
                type: "far",
                color: "warning",
              },
            },
            {
              label: "Observaciones enviadas",
              field: "WITH_CORRECTIONS",
              value: false,
              icon: {
                name: "send",
                type: "far",
                color: "warning",
              },
            },
            {
              label: "En proceso de corrección",
              field: "REVIEW_CORRECTION_IN_PROGRESS",
              value: false,
              icon: {
                name: "file-edit",
                type: "far",
                color: "action",
              },
            },
            {
              label: "Correcciones recibidas",
              field: "REVIEW_CORRECTIONS_RECEIVED",
              value: false,
              icon: {
                name: "envelope",
                type: "far",
                color: "warning",
              },
            },
          ],
        },
        {
          label: "Solicitud de plazo",
          field: "revision-node",
          value: false,
          children: [
            {
              label: "Solicitud de prorroga",
              field: "EXTENSION_REQUEST",
              value: false,
              icon: {
                name: "timer",
                type: "far",
                color: "warning",
              },
            },
            {
              label: "Solicitud de suspensión",
              field: "SUSPENSION_REQUEST",
              value: false,
              icon: {
                name: "circle-stop",
                type: "far",
                color: "warning",
              },
            },
            {
              label: "Suspendida",
              field: "SUSPENDED",
              value: false,
              icon: {
                name: "circle-stop",
                type: "far",
                color: "neutral",
              },
            },
          ],
        },
        {
          label: "Pago",
          field: "payment-node",
          value: false,
          children: [
            {
              label: "Pendiente de pago",
              field: "PAYMENT_PENDING",
              value: false,
              icon: {
                name: "dollar",
                type: "far",
                color: "info",
              },
            },
          ],
        },
        {
          label: "Firma",
          field: "sign-document-node",
          value: false,
          children: [
            {
              label: "Pendiente de firma",
              field: "SIGNATURE_PENDING",
              value: false,
              icon: {
                name: "clock",
                type: "far",
                color: "info",
              },
            },
            {
              label: "En proceso de firma",
              field: "SIGNATURE_IN_PROGRESS",
              value: false,
              icon: {
                name: "arrows-repeat",
                type: "far",
                color: "action",
              },
            },
          ],
        },
        {
          label: "Acciones del sistema",
          field: "general-node",
          value: false,
          children: [
            {
              label: "En proceso",
              field: "PREPARING_PROCESS",
              value: false,
              icon: {
                name: "arrows-repeat",
                type: "far",
                color: "action",
              },
            },
          ],
        },
        {
          label: "Resolución",
          field: "resolution-node",
          value: false,
          children: [
            {
              label: "Aprobado",
              field: "APPROVED",
              value: false,
              icon: {
                name: "check",
                type: "far",
                color: "success",
              },
            },
            {
              label: "Denegado",
              field: "REJECTED",
              value: false,
              icon: {
                name: "times",
                type: "far",
                color: "danger",
              },
            },
            {
              label: "Cancelado",
              field: "CANCELED",
              value: false,
              icon: {
                name: "minus",
                type: "far",
                color: "neutral",
              },
            },
          ],
        },
      ],
    },
  ],
};
